/**
 * Cloud-VM GUI parity — 3c (docs/cloud-vm/gui-parity-plan.md + agent-activity
 * -journal-tdd.md §6). Projects the durable agent activity journal
 * (`agent.history@1`'s `AgentHistoryPage.items`) into renderer {@link ChatMessage}s,
 * so a cloud Furnace build survives a full browser reload: the chat re-hydrates
 * from the journal instead of a blank surface.
 *
 * In cookie/cloud mode `chat:load` is NOT runtime-served (the chat-history.json
 * IPC path is desktop-only), so the journal IS the canonical hydration source
 * there. The projection mirrors how the live `agent:stream` reducer builds
 * messages (same id/role/turnId/toolCalls/streamStatus shape), and crucially
 * carries each journal line's envelope `id` onto the produced message — the Pin
 * #1 shared-id invariant — so a message recovered from the journal de-dupes
 * cleanly against the same message arriving live (merge-by-id in the loader).
 *
 * This module is PURE + dependency-light: it takes the wire events (validated
 * structurally, unknown kinds ignored) and returns messages. It never throws —
 * a malformed line is skipped, mirroring the reader's graceful-degradation.
 */
import type { InlineCheckpoint } from '@volley/crucible-ipc-types/ipc-channels';
import type { ChatMessage, MessageBlock, ToolCallEntry } from './types';
import { checkpointToChatMessage } from './state';

/**
 * Structural view of one journal line as it arrives over the wire from
 * `agent.history@1` (the crucible `AgentJournalEvent` shape, kept decoupled —
 * crucible-ui does not depend on @volley/crucible). Only the fields the
 * projection reads are declared; everything else rides along ignored.
 */
export interface JournalEventLike {
  readonly t?: unknown;
  readonly id?: unknown;
  readonly kind?: unknown;
  readonly turnId?: unknown;
  /** Sub-agent attribution from the envelope (full-history, 2026-06): tags a
   *  text/thinking/tool line produced inside a Task worker. */
  readonly agentId?: unknown;
  readonly payload?: unknown;
}

/** The `agent.history@1` result page (only the fields the client reads).
 *  The canonical field is `items` (runtime `AgentHistoryPage.items`, #2035).
 *  `events` is accepted as a legacy/defensive fallback only — the prod RPC has
 *  never returned it, and reading the wrong name silently projected NOTHING,
 *  which is what made every detached/reattached build render blank. */
export interface AgentHistoryPageLike {
  readonly items?: readonly JournalEventLike[];
  readonly events?: readonly JournalEventLike[];
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};
}

function toMillis(t: unknown): number {
  if (typeof t !== 'string') return 0;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : 0;
}

function stringifyResult(result: unknown): string {
  if (typeof result === 'string') return result;
  try {
    return JSON.stringify(result);
  } catch {
    return String(result);
  }
}

/**
 * Project an `agent.history@1` page (or a bare events array) into chat messages,
 * ordered by the journal's primary sort key (`t`). One assistant message per
 * `turnId`, accreting `toolCalls` from `tool.call`/`tool.result`/`file.edit`
 * lines; one user message per `turn.user`. A turn whose terminal line is absent
 * (the in-flight turn captured mid-build) still yields an assistant message with
 * `streamStatus: 'in-flight'` carrying the activity so far — that is the whole
 * point on a reload while the agent is working.
 */
export function projectAgentHistoryToChatMessages(
  page: AgentHistoryPageLike | readonly JournalEventLike[] | null | undefined,
): ChatMessage[] {
  let events: readonly JournalEventLike[];
  if (Array.isArray(page)) {
    events = page;
  } else if (page && typeof page === 'object') {
    // `agent.history@1` returns the events under `items` (runtime
    // `AgentHistoryPage.items`). Read that first; `events` is a legacy
    // fallback. Reading only `events` (which the RPC never sets) projected an
    // empty timeline on every poll — the root cause of the silent detached
    // build, upstream of the merge/gate fixes.
    const p = page as AgentHistoryPageLike;
    events = (Array.isArray(p.items) ? p.items : Array.isArray(p.events) ? p.events : []) ?? [];
  } else {
    events = [];
  }
  if (events.length === 0) return [];

  // Sort by `t` (the reader's primary sort key) so the projection is stable
  // regardless of per-file/per-device interleaving in the page.
  const sorted = [...events].sort((a, b) => toMillis(a.t) - toMillis(b.t));

  // One assistant message per turnId, built up as activity lines stream past.
  const assistantByTurn = new Map<string, ChatMessage>();
  // callId → the ToolCallEntry we created on tool.call, so tool.result can
  // update it in place.
  const toolCallByCallId = new Map<string, ToolCallEntry>();
  const out: ChatMessage[] = [];
  // Turns for which we saw at least one `turn.text-segment` line. When set, the
  // ordered `blocks` ARE the interleaved play-by-play and `turn.finalised` must
  // NOT prepend a duplicate leading text block — it only stamps `content` +
  // streamStatus. When absent (a pre-full-history journal that only has the
  // concatenated finalText), `turn.finalised` falls back to one leading block.
  const turnsWithSegments = new Set<string>();

  const ensureAssistant = (turnId: string, id: string, t: unknown): ChatMessage => {
    let msg = assistantByTurn.get(turnId);
    if (!msg) {
      msg = {
        id,
        role: 'assistant',
        content: '',
        timestamp: toMillis(t),
        source: 'agent-stream',
        streamStatus: 'in-flight',
        turnId,
        toolCalls: [],
        // Ordered blocks recovered from the journal. Full-history (2026-06): the
        // journal now carries ordered `turn.text-segment` + `turn.thinking` lines
        // alongside `tool.call`/`file.edit`, so the COMPLETE interleaved play-by-
        // play is reconstructable in `seq` order — text, reasoning, and tool
        // cards thread exactly as they streamed live. A pre-full-history journal
        // (only `turn.finalised.finalText`) falls back to one leading text block.
        // De-dupes against live blocks by the shared envelope id.
        blocks: [],
      };
      assistantByTurn.set(turnId, msg);
      out.push(msg);
    }
    return msg;
  };

  for (const ev of sorted) {
    const kind = asString(ev.kind);
    const id = asString(ev.id);
    const turnId = asString(ev.turnId);
    if (!kind || !id) continue; // a line with no kind/id is unprojectable
    const payload = asRecord(ev.payload);

    switch (kind) {
      case 'turn.user': {
        const text = asString(payload.text) ?? '';
        out.push({
          id,
          role: 'user',
          content: text,
          timestamp: toMillis(ev.t),
          turnId: turnId ?? undefined,
        });
        break;
      }
      case 'turn.text-segment': {
        // Full-history (2026-06) — one ordered run of assistant answer text.
        // Append it as a `text` block in journal order so it interleaves with
        // tool / thinking blocks exactly as it streamed. Also accrete into
        // `content` (the legacy concatenation) so a fallback renderer + the
        // markdown-question parser still work.
        if (!turnId) break;
        const text = asString(payload.text);
        if (!text) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        turnsWithSegments.add(turnId);
        const evAgentId = asString(ev.agentId);
        // A sub-agent's text does NOT accrete into the main `content` blob (the
        // canonical answer text is the main thread's); attributed text lives in
        // blocks only. Main-thread text accretes as before.
        if (!evAgentId) msg.content = (msg.content ?? '') + text;
        msg.blocks = [
          ...(msg.blocks ?? []),
          { kind: 'text', text, ...(evAgentId ? { agentId: evAgentId } : {}) },
        ];
        break;
      }
      case 'turn.thinking': {
        // Full-history (2026-06) — one reasoning block, in journal order. Lives
        // in `blocks` only (never `content`, mirroring the live reducer) so it
        // renders as a dim collapsible segment and never resurrects as answer
        // text.
        if (!turnId) break;
        const text = asString(payload.text);
        if (!text) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        const evAgentId = asString(ev.agentId);
        msg.blocks = [
          ...(msg.blocks ?? []),
          { kind: 'thinking', text, ...(evAgentId ? { agentId: evAgentId } : {}) },
        ];
        break;
      }
      case 'subagent.start': {
        // Full-history (2026-06) — inline sub-agent dispatch marker, in journal
        // order. The lifecycle line carries the sub-agent id on `agentId`
        // attribution; the payload has the agentType.
        if (!turnId) break;
        const subAgentId = asString(payload.agentId);
        if (!subAgentId) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        msg.blocks = [
          ...(msg.blocks ?? []),
          {
            kind: 'subagent',
            phase: 'start',
            agentId: subAgentId,
            agentType: asString(payload.agentType) ?? 'sub-agent',
          },
        ];
        break;
      }
      case 'subagent.stop': {
        if (!turnId) break;
        const subAgentId = asString(payload.agentId);
        if (!subAgentId) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        const stopReason = asString(payload.stopReason);
        msg.blocks = [
          ...(msg.blocks ?? []),
          {
            kind: 'subagent',
            phase: 'stop',
            agentId: subAgentId,
            agentType: asString(payload.agentType) ?? 'sub-agent',
            ...(stopReason === 'success' || stopReason === 'interrupted' || stopReason === 'error'
              ? { stopReason }
              : {}),
          },
        ];
        break;
      }
      case 'turn.finalised': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        msg.streamStatus = 'done';
        if (turnsWithSegments.has(turnId)) {
          // Full-history path: the ordered `turn.text-segment` blocks already ARE
          // the answer text in the right places. Keep `content` (accreted from
          // the segments) and do NOT prepend a duplicate leading block.
          const finalText = asString(payload.finalText);
          // Prefer the canonical finalText for `content` when present (it is the
          // authoritative join); the segments already populated blocks.
          if (finalText) msg.content = finalText;
          break;
        }
        // Fallback (pre-full-history journal): only the concatenated finalText
        // exists, so prepend it as ONE leading text block ahead of tool cards.
        const finalText = asString(payload.finalText) ?? msg.content;
        msg.content = finalText;
        if (finalText) {
          msg.blocks = [{ kind: 'text', text: finalText }, ...(msg.blocks ?? [])];
        }
        break;
      }
      case 'turn.errored': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        msg.streamStatus = 'error';
        const message = asString(payload.message) ?? 'The agent turn failed.';
        if (!msg.content) msg.content = message;
        break;
      }
      case 'turn.interrupted': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        msg.streamStatus = 'interrupted';
        if (!msg.content) msg.content = asString(payload.message) ?? '';
        break;
      }
      case 'tool.call': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        const callId = asString(payload.callId) ?? id;
        const entry: ToolCallEntry = {
          id: callId,
          name: asString(payload.toolName) ?? 'tool',
          input: asRecord(payload.toolInput),
          status: 'running',
        };
        toolCallByCallId.set(callId, entry);
        msg.toolCalls = [...(msg.toolCalls ?? []), entry];
        const tcAgentId = asString(ev.agentId);
        msg.blocks = [
          ...(msg.blocks ?? []),
          { kind: 'tool_call', toolCallId: callId, ...(tcAgentId ? { agentId: tcAgentId } : {}) },
        ];
        break;
      }
      case 'tool.result': {
        const callId = asString(payload.callId);
        if (!callId) break;
        const entry = toolCallByCallId.get(callId);
        if (!entry) break; // result with no matching call — skip (out-of-order/partial page)
        entry.status = payload.isError === true ? 'error' : 'success';
        entry.output = stringifyResult(payload.result);
        break;
      }
      case 'file.edit': {
        if (!turnId) break;
        const msg = ensureAssistant(turnId, id, ev.t);
        msg.toolCalls = [
          ...(msg.toolCalls ?? []),
          {
            id,
            name: asString(payload.tool) ?? 'Edit',
            input: {
              path: asString(payload.path) ?? '',
              operation: asString(payload.operation) ?? 'modify',
            },
            status: 'success',
          },
        ];
        msg.blocks = [...(msg.blocks ?? []), { kind: 'tool_call', toolCallId: id }];
        break;
      }
      case 'activity.checkpoint': {
        // GAP-6 AC#4 — checkpoints are part of the activity timeline, not just
        // chat bubbles. The journal line `id` EQUALS the live `activity:checkpoint`
        // push id (shared-id), and the payload IS the InlineCheckpoint. Build the
        // row with the SAME `checkpointToChatMessage` the live reducer uses, so a
        // reload-restored checkpoint is byte-identical to the live one (and the
        // reducer's id-dedup collapses the two). Skip a malformed payload.
        const cp = payload as unknown as InlineCheckpoint;
        if (typeof cp.id === 'string' && typeof cp.timestamp === 'number') {
          out.push(checkpointToChatMessage(cp));
        }
        break;
      }
      default:
        // run.open / turn.warning / dev.preview / publish.result / build.* /
        // unknown kinds — not chat-message content.
        break;
    }
  }

  // Drop empty assistant placeholders (a turn that produced no text and no
  // tool activity — nothing for the user to see).
  return out.filter(
    (m) => m.role !== 'assistant' || m.content.length > 0 || (m.toolCalls?.length ?? 0) > 0,
  );
}

/**
 * Merge journal-projected messages into the (possibly empty, in cloud mode)
 * `chat:load` set, de-duped by message `id`. The shared-id invariant (Pin #1)
 * means a turn present in both layers collapses to one message; `chat:load`
 * wins on a conflict (it is the post-save canonical copy). Order follows the
 * combined timeline by `timestamp`, then stable by original index.
 */
export function mergeHydrationMessages(
  saved: readonly ChatMessage[],
  projected: readonly ChatMessage[],
): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const m of saved) byId.set(m.id, m);
  for (const m of projected) if (!byId.has(m.id)) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
}
